import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriminalAppellateWizardContext } from '../../../hooks/useCaseRequestWizard';
import { CONTACT_NUMBER_LENGTH, contactNumberError } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { CriminalAppellateWizardActions } from './CriminalAppellateWizardActions';
import { criminalAppellateWizardPath, CriminalAppellateWizardShell, FieldError, inpClass } from './CriminalAppellateWizardShell';
import { validateCriminalAppellateStep } from './criminalAppellateStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_NAME = 255;
const MAX_DESC = 2000;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

export default function CriminalAppellateStep2PartyDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCriminalAppellateWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateCriminalAppellateStep(2, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <CriminalAppellateWizardShell currentStep={2} draft={draft}>
      <SectionCard title={f.sectionPartyDetails}>
        {/* Row 1 — Names */}
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.plaintiffApplicantName} <span className="req">*</span>
            </label>
            <input
              id="capp-plaintiff"
              className={inpClass(Boolean(errors.plaintiffApplicantName))}
              type="text"
              value={draft.plaintiffApplicantName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, plaintiffApplicantName: maxLen('plaintiffApplicantName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterPlaintiffApplicantName}
            />
            <FieldError message={errors.plaintiffApplicantName} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.defendantRespondentName} <span className="req">*</span>
            </label>
            <input
              id="capp-defendant"
              className={inpClass(Boolean(errors.defendantRespondentName))}
              type="text"
              value={draft.defendantRespondentName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, defendantRespondentName: maxLen('defendantRespondentName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterDefendantRespondentName}
            />
            <FieldError message={errors.defendantRespondentName} />
          </div>
        </div>

        {/* Row 2 — Client type radio (single selection: Government OR Corporation) */}
        <div className="ccr-fg" id="capp-clientType" style={{ marginBottom: 14 }}>
          <label>
            {f.selectOneOfFollowing} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="capp-clientType"
                checked={draft.clientType === 'government'}
                onChange={() => {
                  setDraft((d) => ({ ...d, clientType: 'government' }));
                  clearErr('clientType');
                }}
              />
              <span>{f.governmentMinistryDepartmentAlt}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="capp-clientType"
                checked={draft.clientType === 'corporation'}
                onChange={() => {
                  setDraft((d) => ({ ...d, clientType: 'corporation' }));
                  clearErr('clientType');
                }}
              />
              <span>{f.publicCorporationStatutoryAlt}</span>
            </label>
          </div>
          <FieldError message={errors.clientType} />
        </div>

        {/* Row 3 — Other Involved Institutions (optional) */}
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.otherInvolvedInstitutions}</label>
          <textarea
            className={inpClass(Boolean(errors.otherInvolvedInstitutions))}
            rows={3}
            value={draft.otherInvolvedInstitutions}
            onChange={(e) =>
              setDraft((d) => ({ ...d, otherInvolvedInstitutions: maxLen('otherInvolvedInstitutions', e.target.value, MAX_DESC) }))
            }
            placeholder={f.otherRelatedInstitutionsPublicPrivateHint}
          />
          <FieldError message={errors.otherInvolvedInstitutions} />
        </div>

        {/* Row 4 — Legal officer */}
        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <div className="ccr-fg">
            <label>
              {f.legalOfficerNameAlt} <span className="req">*</span>
            </label>
            <input
              id="capp-legalOfficer"
              className={inpClass(Boolean(errors.legalOfficerName))}
              type="text"
              value={draft.legalOfficerName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, legalOfficerName: maxLen('legalOfficerName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterLegalOfficerNameInCharge}
            />
            <FieldError message={errors.legalOfficerName} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.contactNumber} <span className="req">*</span>
            </label>
            <input
              id="capp-legalContact"
              className={inpClass(Boolean(errors.legalOfficerContact))}
              type="text"
              inputMode="numeric"
              value={draft.legalOfficerContact}
              onChange={(e) => {
                setDraft((d) => ({
                  ...d,
                  legalOfficerContact: e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT) }));
                clearErr('legalOfficerContact');
              }}
              onBlur={() => {
                const err = contactNumberError(draft.legalOfficerContact);
                if (err) setErrors((e) => ({ ...e, legalOfficerContact: err }));
              }}
              placeholder={f.contactNumberExample}
            />
            <FieldError message={errors.legalOfficerContact} />
          </div>
        </div>
      </SectionCard>

      <CriminalAppellateWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={2}
        validateBeforeSave={validate}
        onBack={() => nav(criminalAppellateWizardPath(1, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(criminalAppellateWizardPath(3, draft.draftRequestId));
        }}
      />
    </CriminalAppellateWizardShell>
  );
}
